import type { ChangeLogEntryDto, ChangeLogFieldChangeDto } from '../../../../../api/created/changeLog.ts';
import type { UserDto } from '../../../../../api/generated/model';
import type {
  ChangeHistoryEntryData,
  HistoryAction,
  HistoryFieldChange,
} from '../../../../../components/history/historyTypes.ts';
import { resolveChangedByName } from '../../../../../utils/user.ts';

export type MedicationFieldChange = HistoryFieldChange;

export type MedicationHistoryEntry = ChangeHistoryEntryData & {
  id: string;
  action: HistoryAction;
  actionLabel: string;
  changedByName: string;
};

export type MedicationHistoryMappingContext = {
  users: UserDto[];
};

const MEDICATION_FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  is_active: 'Aktiv',
  isActive: 'Aktiv',
};

const ACTION_LABELS: Record<HistoryAction, string> = {
  created: 'wurde erstellt von',
  updated: 'wurde geändert von',
  deleted: 'wurde gelöscht von',
};

function mapOperationToAction(operation: string): HistoryAction {
  const normalizedOperation = operation.trim().toUpperCase();

  if (normalizedOperation.includes('CREATE') || normalizedOperation.includes('INSERT')) {
    return 'created';
  }

  if (normalizedOperation.includes('DELETE') || normalizedOperation.includes('REMOVE')) {
    return 'deleted';
  }

  return 'updated';
}

function getMedicationFieldLabel(fieldName: string) {
  return MEDICATION_FIELD_LABELS[fieldName] ?? fieldName;
}

function formatBooleanValue(value: string) {
  const normalizedValue = value.trim().toLowerCase();

  if (normalizedValue === 'true' || normalizedValue === '1') {
    return 'Ja';
  }

  if (normalizedValue === 'false' || normalizedValue === '0') {
    return 'Nein';
  }

  return value;
}

export function formatMedicationFieldValue(fieldName: string, value: string | null | undefined) {
  if (value == null || !value.trim()) {
    return value ?? '';
  }

  switch (fieldName) {
    case 'is_active':
    case 'isActive':
      return formatBooleanValue(value);
    default:
      return value;
  }
}

function mapFieldChange(change: ChangeLogFieldChangeDto): HistoryFieldChange {
  return {
    fieldLabel: getMedicationFieldLabel(change.fieldName),
    oldValue: formatMedicationFieldValue(change.fieldName, change.oldValue),
    newValue: formatMedicationFieldValue(change.fieldName, change.newValue),
  };
}

export function mapChangeLogEntryToMedicationHistoryEntry(
  entry: ChangeLogEntryDto,
  context: MedicationHistoryMappingContext,
): MedicationHistoryEntry {
  const action = mapOperationToAction(entry.operation);

  return {
    id: entry.changeSetId,
    date: entry.changedAt,
    action,
    actionLabel: ACTION_LABELS[action],
    changedByName: resolveChangedByName(entry.changedBy, context.users),
    changes: entry.changes.map((change) => mapFieldChange(change)),
  };
}

export function mapChangeLogEntriesToMedicationHistory(
  entries: ChangeLogEntryDto[],
  context: MedicationHistoryMappingContext,
): MedicationHistoryEntry[] {
  return entries.map((entry) => mapChangeLogEntryToMedicationHistoryEntry(entry, context));
}
